import log from 'loglevel';
/**
 * Wrapper over a standard fetch function to add basic sanitization,
 * logging and common headers
 *
 * @param {String|Object} url A URL for fetching data | a service object with url property
 * @param {Object} options A config object for fetch
 */

const Fetch = async (url, options = {}) => {
  const mimeType = options.mimeType || 'application/json';
  const defaultOptions = {
    method: 'GET',
    mode: 'cors',
    cache: 'default',
    credentials: 'same-origin',
  };

  const defaultHeaders = mimeType === 'multipart/form-data' ? {} : {
    'content-type': mimeType,
  };

  // extract headers from options
  const { headers, ...optionsWithoutHeaders } = options;

  // combine headers and create a Header instance
  const requestHeaders = new Headers({ ...defaultHeaders, ...headers });

  const requestOptions = { ...defaultOptions, ...optionsWithoutHeaders, headers: requestHeaders };

  // check for url object
  if (typeof url === 'object' && url.url) {
    url = url.url;
  }

  /**
   * Create a request object
   * @link https://developer.mozilla.org/en-US/docs/Web/API/Request/Request
   */
  const request = new Request(url, requestOptions);
  // log.info('Fetch: options/request', requestOptions, request);

  /**
   * Return fetch
   * @link https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch
   */
  switch (mimeType) {
    case 'application/json':
      return fetch(request)
        .then(handleStatus)
      //.then(handleJSON)
        .then(handleResponse)
        .catch(error => {
          if (error.name !== 'AbortError') {
            log.error('Fetch: Fail', { error });
          }

          throw error;
        });
    case 'text/csv':
      return fetch(request)
        .then(handleStatus)
      //.then(handleText)
        .then(handleResponse)
        .catch(error => {
          log.error('Fetch: Fail', { error, request });
          throw new Error(error);
        });
    default:
      return fetch(request)
        .then(handleStatus)
      //.then(handleBinary)
        .then(handleResponse)
        .catch(error => {
          log.error('Fetch: Fail', { error, request });
        });
   }

  function handleStatus(response) {
    if (!response.ok) {
      log.error('Fetch: Not OK response received', { response });
      throw { message: 'Fetch: Call returned with NOT OK response', response};
    }

    return response;
  }

  function handleJSON(response) {
    return response
      .json()
      .then(json => {
        return json;
      })
      .catch(error => {
        log.error('Fetch: JSON parsing failed', { response });
        throw new Error('JSON parsing failed in fetch call');
      });
  }

  function handleText(response) {
    return response
      .text()
      .then(text => {
        return text;
      })
      .catch(error => {
        log.error('Fetch: Text parsing failed', { response });
        throw new Error('Text response failed in fetch call');
      });
  }

  function handleBinary(response) {
    return response
      .blob()
      .then(blob => {
        return blob;
      })
      .catch(error => {
        log.error('Fetch: Blob parsing failed', { response });
        throw new Error('Blob response failed in fetch call');
      });
  }

  function handleResponse(response) {
    // log.info('Fetch: Success', response);
    return response;
  }
};

const withData = (func, method) => {
  return function (url, options, data) {
    const defaultOptions = {
      method,
      body: JSON.stringify(data),
    };
    return func.call(this, url, { ...defaultOptions, ...options });
  };
};

const Post = withData(Fetch, 'POST');
const Put = withData(Fetch, 'PUT');
const Patch = withData(Fetch, 'PATCH');
const Delete = withData(Fetch, 'DELETE');

const FetchAPI = {
  Get: Fetch,
  Post,
  Put,
  Patch,
  Delete,
};

export default FetchAPI;



// WEBPACK FOOTER //
// ./src/helpers/fetchAPI.js